import React, { ReactNode, useEffect, useRef, useState } from "react";
import IconDownArrowHead from "assets/icons/IconDownArrowHead";
import { classNames, labelHelper } from "helpers/themeHelpers";
import useFilterListByTerm from "hooks/useFilterListByTerm";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { DropdownFilterCont, ModalContent } from "./nativeAutocomplete.styled";
import { IItemCommon } from "models/base/Generics";
import Modal from "../../Modal";
import IconClose from "assets/icons/IconClose";
import IconSearch from "assets/icons/IconSearch";
import IconClear from "assets/icons/IconClear";
import { useTheme } from "styled-components";

interface IProps<T> {
  value?: string;
  options: T[];
  renderOption: (
    option: T,
    onClick: (e: any) => void,
    filterTerm?: string
  ) => JSX.Element;
  onSelect?: (option: T, e?: any) => void;
  selectedItem?: T;
  dropdownIcon?: ReactNode;
  getOptionLabel?: (option: T) => JSX.Element;
  placeholder: string;
  elementsClassNames?: { classNameOfUl?: (option?: T) => string };
  inputWithRadius?: boolean;
  inputWithBorder?: boolean;
  filterCb?: (filterTerm: string) => T[] | Promise<T[]>;
  parentContainerClass?: string;
  classname?: string;
  inputId?: string;
  mobileModal?: boolean;
  isBottom?: boolean;
  label?: string;
  validationText?: string;
  isDisabled?: boolean;
  onFilterInputChange?: () => void;
}

function NativeAutoComplete<T extends IItemCommon>(
  props: IProps<T>
): JSX.Element {
  const {
    options,
    selectedItem: selectedItem__default,
    dropdownIcon,
    placeholder,
    elementsClassNames,
    renderOption,
    onSelect,
    inputWithBorder = true,
    inputWithRadius = true,
    filterCb,
    parentContainerClass = "",
    label = "",
    value,
    classname,
    inputId = "dropdown",
    mobileModal = true,
    isBottom = false,
    validationText,
    isDisabled,
    onFilterInputChange
  } = props;
  const { classNameOfUl = () => "" } = elementsClassNames || {};
  const [open, setOpen] = useState(false);
  const { filterTerm, onFilterTermChange, filteredItems } =
    useFilterListByTerm<T>(options, filterCb); //!open;
  const refContainer = useRef<null | HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState(selectedItem__default);
  const [lengthTyping, setLengthTyping] = useState<number>(null);
  const { isMobile, isDesktop } = useTheme();
  useEffect(() => {
    setSelectedItem(selectedItem__default);
  }, [selectedItem__default]);

  useOnClickOutside(refContainer, () => setOpen(false));

  const onInputFocus = () => setOpen(true);

  const responsiveWidthInput = () => {
    return labelHelper(selectedItem) && selectedItem.id !== -2
      ? labelHelper(selectedItem)?.length + 2
      : placeholder?.length;
  };

  return (
    <DropdownFilterCont
      open={open}
      ref={isDesktop ? refContainer : null}
      inputWithBorder={inputWithBorder}
      inputWidth={
        lengthTyping
          ? lengthTyping < responsiveWidthInput()
            ? responsiveWidthInput()
            : lengthTyping
          : responsiveWidthInput()
      }
      className={parentContainerClass}
    >
      {label && <label className="font-14 bold mb-8 block">{label}</label>}
      <div
        {...classNames(
          classname,
          "dropDownWrapper flex alignItems width-100 relative",
          inputWithRadius && "radius-8",
          inputWithBorder && "ps-8 pe-16 border"
        )}
      >
        {dropdownIcon ? dropdownIcon : null}
        {isDesktop ? (
          <>
            <input
              disabled={isDisabled}
              id={inputId}
              type="text"
              value={value ? value : filterTerm}
              placeholder={
                labelHelper(selectedItem) && selectedItem.id !== -2
                  ? labelHelper(selectedItem)
                  : placeholder
              }
              onChange={(e) => {
                onFilterTermChange(e);
                setLengthTyping(e.target.value.length);
                onFilterInputChange && onFilterInputChange();
              }}
              onFocus={onInputFocus}
              {...classNames(
                dropdownIcon && "ps-8",
                "border-0 flex-1 height-50 width-100"
              )}
            />
            <IconDownArrowHead
              onClick={() => setOpen(!open)}
              {...classNames("pointer", open && !isDisabled && "rotateUp")}
            />
            <ul
              {...classNames(
                "absolute whiteBg withScroll fullShadow nativeAutoCompleteList radius-8",
                inputWithBorder && "width-100",
                !inputWithBorder && "noWrap",
                classNameOfUl(),
                open ? "block" : "hide"
              )}
              ref={(el) => {
                if (el) el.scrollTop = 0;
              }}
            >
              {filteredItems?.map((item, index) =>
                renderOption(
                  item,
                  (e) => {
                    onSelect && onSelect(item, e);
                    // @ts-ignore
                    onFilterTermChange({ target: { value: "" } });
                    setSelectedItem(item);
                    setOpen(false);
                    !inputWithBorder && responsiveWidthInput();
                  },
                  filterTerm
                )
              )}
            </ul>
          </>
        ) : (
          <>
            <div
              className="width-100 height-50 flex alignItems darkGrayColor flexSpaceBetween"
              onClick={() => setOpen(!open)}
            >
              {labelHelper(selectedItem) && selectedItem.id !== -2
                ? labelHelper(selectedItem)
                : placeholder}
              <span onClick={() => setOpen(!open)} className="pointer">
                <IconDownArrowHead
                  {...classNames(open && "rotateUp")}
                />
              </span>
            </div>
            <Modal
              open={open}
              onClose={() => setOpen(false)}
              isBottom={isBottom}
              isFullScreen={!isBottom}
            >
              <ModalContent>
                <div className="sticky p-16 whiteBg">
                  <div className="flex alignItems flexSpaceBetween font-17 mb-16">
                    <button className="iconClose flex alignItems justifyContent pointer">
                      <IconClose
                        onClick={() => setOpen(false)}
                        className="pointer"
                      />
                    </button>
                  </div>
                  <div className="flex alignItems fullShadow height-50 ps-8 pe-8 gap-10 radius-8">
                    <label htmlFor={inputId} className="flex alignItems">
                      <IconSearch fill={"gray"} />
                    </label>
                    <input
                      type="text"
                      id={inputId}
                      placeholder={placeholder}
                      onChange={(e) => {
                        onFilterTermChange(e);
                        setLengthTyping(e.target.value.length);
                      }}
                      className="width-100 border-0"
                    />
                    {lengthTyping && lengthTyping > 0 ? (
                      <div
                        onClick={() => {
                          setLengthTyping(0);
                          let input = document.getElementById(
                            inputId
                          ) as HTMLInputElement;
                          input.value = "";
                          // @ts-ignore
                          onFilterTermChange({ target: { value: "" } });
                        }}
                      >
                        <button className="iconClear flex alignItems iconClear">
                          <IconClear />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <ul
                  {...classNames(
                    "whiteBg withScroll fullShadow nativeAutoCompleteList width-100",
                    isBottom && "bottomHeight overflowY-scroll",
                    !inputWithBorder && "noWrap",
                    classNameOfUl(),
                    open ? "block" : "hide"
                  )}
                  ref={(el) => {
                    if (el) el.scrollTop = 0;
                  }}
                >
                  {filteredItems?.map((item, index) =>
                    renderOption(
                      item,
                      (e) => {
                        onSelect && onSelect(item, e);
                        setSelectedItem(item);
                        setOpen(false);
                        !inputWithBorder && responsiveWidthInput();
                      },
                      filterTerm
                    )
                  )}
                </ul>
              </ModalContent>
            </Modal>
          </>
        )}
      </div>
      {validationText && (
        <span className="redColor font-14 block mt-8">{validationText}</span>
      )}
    </DropdownFilterCont>
  );
}

export default NativeAutoComplete;
